import React from 'react';
import { Delete, RotateCcw, Check } from 'lucide-react';
import { playTick } from '../utils/synthesizer';

interface VirtualKeyboardProps {
  value: string;
  onChange: (newValue: string) => void;
  onEnter: () => void;
  maxLength?: number;
}

export const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  value,
  onChange,
  onEnter,
  maxLength = 15
}) => {
  const keys = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', '-'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', '_', '@', '.']
  ];

  const handleKeyPress = (char: string) => {
    playTick();
    if (value.length < maxLength) {
      onChange(value + char);
    }
  };

  const handleDelete = () => {
    playTick();
    onChange(value.slice(0, -1));
  };

  const handleClear = () => {
    playTick();
    onChange('');
  };

  return (
    <div 
      className="w-full max-w-sm sm:max-w-md md:max-w-xl aspect-[2/1] flex flex-col justify-between backdrop-blur-md border-2 rounded-2xl p-1 min-[350px]:p-1.5 sm:p-2.5 md:p-3 shadow-2xl select-none mx-auto" 
      style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}
    >
      <div className="flex flex-col h-full justify-between gap-0.5 min-[350px]:gap-0.75 sm:gap-1.5 md:gap-2">
        {/* Row 1, 2, 3 Letters */}
        {keys.map((row, rowIndex) => (
          <div key={rowIndex} className="flex flex-1 justify-center gap-0.5 min-[350px]:gap-0.75 sm:gap-1.5">
            {row.map((char) => (
              <button
                type="button"
                key={char}
                onClick={() => handleKeyPress(char)}
                className="flex-1 h-full rounded-md sm:rounded-xl font-mono font-black transition-all shadow flex items-center justify-center cursor-pointer select-none border border-[#db8504]/20 hover:bg-[#db8504]/20 active:scale-95 duration-100"
                style={{ 
                  backgroundColor: '#fff5e8', 
                  color: '#5c390d',
                  fontSize: 'clamp(8px, 3.2vw, 18px)'
                }}
              >
                {char}
              </button>
            ))}
          </div>
        ))}

        {/* Row 4: Controls and Space */}
        <div className="flex flex-1 justify-center gap-0.5 min-[350px]:gap-0.75 sm:gap-1.5">
          {/* RESET / CLEAR */}
          <button
            type="button"
            onClick={handleClear}
            className="flex-[2] min-w-0 h-full border-2 rounded-md sm:rounded-xl font-black font-sans flex items-center justify-center gap-0.5 sm:gap-1 px-1 sm:px-2 cursor-pointer transition select-none hover:bg-red-50"
            style={{ 
              backgroundColor: '#fff5e8', 
              color: '#991b1b', 
              borderColor: '#ef4444',
              fontSize: 'clamp(7px, 2.3vw, 14px)'
            }}
          >
            <RotateCcw 
              style={{ 
                width: 'clamp(8px, 2.5vw, 14px)', 
                height: 'clamp(8px, 2.5vw, 14px)',
                color: '#991b1b'
              }} 
            />
            <span className="truncate">Clear</span>
          </button>

          {/* SPACEBAR */}
          <button
            type="button"
            onClick={() => handleKeyPress(' ')}
            className="flex-[3.5] min-w-0 h-full rounded-md sm:rounded-xl font-black font-sans transition-all shadow flex items-center justify-center px-1 cursor-pointer select-none border border-[#db8504]/20 hover:bg-amber-50"
            style={{ 
              backgroundColor: '#fff5e8', 
              color: '#5c390d',
              fontSize: 'clamp(7px, 2.3vw, 14px)'
            }}
          >
            SPACE
          </button>

          {/* DELETE */}
          <button
            type="button"
            onClick={handleDelete}
            className="flex-[1.3] min-w-0 h-full rounded-md sm:rounded-xl font-black transition-all shadow flex items-center justify-center px-1 cursor-pointer select-none border border-[#db8504]/20 hover:bg-amber-50"
            style={{ backgroundColor: '#fff5e8' }}
          >
            <Delete 
              style={{ 
                width: 'clamp(10px, 2.8vw, 16px)', 
                height: 'clamp(10px, 2.8vw, 16px)',
                color: '#5c390d' 
              }} 
              className="animate-none" 
            />
          </button>

          {/* SAVE / ENTER */}
          <button
            type="button"
            onClick={onEnter}
            disabled={!value.trim()}
            className="flex-[2.2] min-w-0 h-full rounded-md sm:rounded-xl font-black font-sans flex items-center justify-center gap-0.5 sm:gap-1 px-1 sm:px-2 shadow-lg cursor-pointer transition select-none border"
            style={{ 
               backgroundColor: value.trim() ? '#db8504' : '#fff5e830', 
               color: value.trim() ? '#fff5e8' : '#fff5e860', 
               borderColor: '#db8504',
               fontSize: 'clamp(7px, 2.3vw, 14px)'
            }}
          >
            <Check 
              strokeWidth={3} 
              style={{ 
                width: 'clamp(8px, 2.5vw, 14px)', 
                height: 'clamp(8px, 2.5vw, 14px)',
                color: value.trim() ? '#fff5e8' : '#fff5e860' 
              }} 
            />
            <span className="truncate">Play</span>
          </button>
        </div>
      </div>
    </div>
  );
};
